import { useState } from 'react'
import { useAsyncAction } from './useAsyncAction'
import { useClient } from '../providers/supabase'

/**
 * Password-change state + submit action shared by the standalone
 * `ChangePasswordForm` (settings) and the combined onboarding password + 2FA
 * step, so the validation and update-call logic don't drift between the two
 * call sites. Built on `useAsyncAction`, which reports failures to Sentry
 * automatically.
 */
export function useChangePassword() {
  const client = useClient()
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('')

  const [state, submit, reset] = useAsyncAction(async () => {
    if (!newPassword) { throw new Error('New Password is required') }
    if (newPassword !== newPasswordConfirmation) { throw new Error('Confirmation does not match') }

    const resp = await client.auth.updateUser({ password: newPassword })
    if (resp.error) { throw resp.error }
    return resp.data
  }, [client, newPassword, newPasswordConfirmation])

  // Only flag a mismatch once both fields are non-empty — a blank
  // confirmation isn't wrong yet, just incomplete.
  const confirmationIsInvalid = !!newPassword && !!newPasswordConfirmation &&
    newPassword !== newPasswordConfirmation

  return {
    newPassword,
    setNewPassword,
    newPasswordConfirmation,
    setNewPasswordConfirmation,
    confirmationIsInvalid,
    submitting: state.loading,
    error: state.error,
    submit,
    reset,
  }
}
